<template>
	<div
		class="moments-stories-player-overlay"
		:class="overlayClass"
	>
		<div class="moments-stories-player__pointers">
			<div
				v-for="(item, index) in storyItems"
				:key="`${stageType}-pointer-${item.id}-${index}`"
				class="moments-stories-player__pointer"
			>
				<span
					class="moments-stories-player__pointer-progress"
					:style="pointerStyle(index)"
				/>
			</div>
		</div>
		<div class="moments-stories-player__head">
			<div class="moments-stories-player__head-left">
				<span class="moments-stories-player__dual-avatar">
					<img
						:src="group?.participants?.[0]?.imageUrl || ''"
						:alt="group?.participants?.[0]?.clubCode || ''"
						class="moments-stories-player__avatar -first"
					>
					<img
						:src="group?.participants?.[1]?.imageUrl || ''"
						:alt="group?.participants?.[1]?.clubCode || ''"
						class="moments-stories-player__avatar -second"
					>
				</span>
				<div class="moments-stories-player__meta">
					<div class="moments-stories-player__name">
						{{ groupName }}
					</div>
					<div class="moments-stories-player__time">
						{{ groupDateLabel }}
					</div>
				</div>
			</div>
			<div class="moments-stories-player__head-controls">
				<Button
					class="moments-stories-player__head-control"
					modifier="borderless"
					theme="dark"
					size="small"
					icon="e-remove"
					icon-size="xl"
					title="Close"
					@click="$emit('close')"
				/>
				<Button
					class="moments-stories-player__head-control"
					modifier="borderless"
					theme="dark"
					size="small"
					:icon="isMuted ? 'sound-off' : 'sound-on'"
					icon-size="xl"
					title="Toggle sound"
					@click="$emit('toggle-mute')"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Button from '@shared/components/Button';

export default {
	name:       'MomentsStoriesPlayerOverlay',
	components: {
		Button,
	},
	props: {
		stageType: {
			type:    String,
			default: 'current',
		},
		stageClass: {
			type:    [String, Array, Object],
			default: '',
		},
		storyItems: {
			type:    Array,
			default: () => [],
		},
		activeItemIndex: {
			type:    Number,
			default: 0,
		},
		activeItemProgress: {
			type:    Number,
			default: 0,
		},
		group: {
			type:    Object,
			default: null,
		},
		isMuted: {
			type:    Boolean,
			default: false,
		},
	},
	computed: {
		overlayClass() {
			return [`-${this.stageType}`, this.stageClass];
		},
		groupName() {
			return (this.group?.participants || []).map(participant => participant.clubCode).join(' - ');
		},
		groupDateLabel() {
			return this.formatDateLabel(this.group?.createdAt);
		},
	},
	methods: {
		pointerStyle(index) {
			let progress = 0;
			if (index < this.activeItemIndex) {
				progress = 1;
			} else if (index === this.activeItemIndex) {
				progress = this.activeItemProgress;
			}

			return {
				transform: `scaleX(${Math.max(0, Math.min(1, progress))})`,
			};
		},
		formatDateLabel(dateValue) {
			if (!dateValue) {
				return '';
			}

			const parsedDate = new Date(dateValue);
			if (Number.isNaN(parsedDate.getTime())) {
				return '';
			}

			return new Intl.DateTimeFormat(undefined, {
				year:  'numeric',
				month: '2-digit',
				day:   '2-digit',
			}).format(parsedDate);
		},
	},
};
</script>

<style lang="scss">
@import '@shared/sass/shared-variables';

/* stylelint-disable */
.moments-stories-player-overlay {
	position: absolute;
	inset: 0;
	background: transparent;
	pointer-events: none;
	transition: transform 260ms ease;

	&.-current,
	&.-incoming {
		@include z-index(head, moments-stories-player);
	}

	&.-current {
		transform: translateX(0);

		&.-to-next.-transitioning {
			transform: translateX(-100%);
		}

		&.-to-prev.-transitioning {
			transform: translateX(100%);
		}

		&.-pinned {
			transform: translateX(0);
			transition: none;
		}
	}

	&.-incoming {
		transform: translateX(100%);

		&.-to-prev {
			transform: translateX(-100%);
		}

		&.-transitioning {
			transform: translateX(0);
		}

		&.-pinned {
			transform: translateX(0);
			transition: none;
		}

		&.-repositioning {
			transition: none;
		}
	}

	.moments-stories-player__head-controls,
	.moments-stories-player__head-control {
		pointer-events: auto;
	}
}

.moments-stories-player__pointers {
	@include z-index(pointers, moments-stories-player);
	position: absolute;
	top: $sp5;
	left: $sp5;
	right: $sp5;
	display: grid;
	grid-auto-flow: column;
	grid-auto-columns: 1fr;
	gap: 4px;
}

.moments-stories-player__pointer {
	height: 2px;
	background-color: var(--color-grey-500);
	border-radius: 2px;
	overflow: hidden;
}

.moments-stories-player__pointer-progress {
	display: block;
	width: 100%;
	height: 100%;
	background: var(--color-white);
	transform-origin: left center;
}

.moments-stories-player__head {
	@include z-index(head, moments-stories-player);
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	padding: $sp7 $sp4-5 $sp2;
	color: #fff;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.moments-stories-player__head-left {
	@include z-index(head-content, moments-stories-player);
	position: relative;
	display: flex;
	align-items: center;
	min-width: 0;
}

.moments-stories-player__head-controls {
	@include z-index(head-controls, moments-stories-player);
	position: relative;
	display: inline-flex;
	flex-direction: column;
	align-items: center;
	gap: $sp4;
}

.moments-stories-player__meta {
	min-width: 0;
	margin-left: 8px;
}

.moments-stories-player__name {
	font-weight: 500;
	font-size: 13px;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: min(340px, 45vw);
}

.moments-stories-player__time {
	font-size: 12px;
	line-height: 1.2;
	opacity: 0.85;
}

.moments-stories-player__head-control {
	width: 40px;
	height: 40px;
	min-width: 40px;
	padding: 0;
	border-radius: 50%;
	color: #fff !important;
	backdrop-filter: blur(2px);
}

.moments-stories-player__dual-avatar {
	position: relative;
	display: inline-flex;
	width: 50px;
	height: 32px;
}

.moments-stories-player__avatar {
	position: absolute;
	width: 32px;
	height: 32px;
	object-fit: cover;

	&.-first {
		@include z-index(avatar-first, moments-stories-player);
		left: 0;
		top: 2px;
	}

	&.-second {
		@include z-index(avatar-second, moments-stories-player);
		right: 0;
		top: 2px;
	}
}

@media (max-width: 1024px) {
	.moments-stories-player__name {
		max-width: calc(100vw - 140px);
	}
}
/* stylelint-enable */
</style>
